import {
    driveRepository,
    fragmentRepository,
    playRepository,
    statsRepository
} from "../storage/storage";
import Drive from "../sports/football/drive";
import DriveEndType from "../sports/football/enums/drive-end-type";
import FootballState from "../sports/football/state/football-state";
import {
    getPeriodStartSeconds,
    getTimeDifference
} from "../sports/football/utils/context-utils";
import DriveStartType from "../sports/football/enums/drive-start-type";
import { isNumber } from "./common/object-util";
import {
    deriveDriveLinks,
    END_DRIVE_TIME_STATE
} from "./sync/normalize-legacy-drives";

class DriveService {
    async getById(driveId) {
        return driveRepository.getById(driveId);
    }

    async _put(drive) {
        drive.id = await driveRepository.put(drive);
        return drive;
    }

    async _delete(driveId) {
        driveRepository.delete(driveId);
    }

    /**
     * Retrieve the first incomplete drive.
     * @param {string} gameId
     * @returns {Promise<Drive|null>}
     */
    async getCurrentDrive(gameId) {
        return (
            (await driveRepository.where({ gameId: gameId }).toArray())
                .filter((d) => !d.completed)
                .sort((a, b) => a.index - b.index)
                .shift() || null
        );
    }

    /**
     * Retrieve the previous drive using the provided index.
     * @param {string} gameId
     * @param {number} index
     * @returns {Promise<Drive|null>}
     */
    async getPreviousDriveByIndex(gameId, index) {
        if (index > 1) {
            return driveRepository
                .where({ gameId: gameId })
                .filter((d) => d.index === index - 1)
                .first();
        }
        return null;
    }

    /**
     * Retrieve the drive that comes after the drive corresponding the provided driveId.
     * @param {string} driveId
     * @returns {Promise<Drive|null>}
     */
    async getNextDrive(driveId) {
        const drive = await this.getById(driveId);
        if (drive) {
            return driveRepository
                .where({ gameId: drive.gameId })
                .filter((d) => d.index === drive.index + 1)
                .first();
        }
        return null;
    }

    async getPreviousDrive(driveId) {
        const drive = await this.getById(driveId);
        if (drive) {
            return driveRepository
                .where({ gameId: drive.gameId })
                .filter((d) => d.index === drive.index - 1)
                .first();
        }
        return null;
    }

    async getLastDrive(gameId) {
        const drives = await driveRepository
            .where({ gameId: gameId })
            .sortBy("index");

        const [last] = drives.slice(-1);

        return last;
    }

    async getLastDriveEndingPlay(gameId) {
        const game = await this.getByGameId(gameId, true);
        const [lastDrive] = game ? game.slice(-1) : [];

        return (lastDrive?.plays || []).slice(-1)[0];
    }

    /**
     * Recompute `endTime`, `endPeriod`, and `totalTime` for every drive so
     * inter-drive clock gaps are credited to the drive that just ended.
     * Each drive's end is stamped from the next drive's start (closing any
     * gap from PAT/kickoff/etc.); the last drive's end falls back to the
     * current game clock. The first drive of regulation is rolled back to
     * period start so the opening kickoff is credited to the receiving
     * team (per NFL/NCAA TOP convention). Mutates each drive instance in
     * place and returns the sorted array. Used by boxscore generation so
     * per-team time of possession sums to the elapsed game length.
     */
    computeDriveTotalTimes(drives, gameContext) {
        const { minutesByPeriod, periodsInRegulation } =
            gameContext.gameConfig.general;
        const periodLengthMs = minutesByPeriod * 60 * 1000;
        const secondHalfStart = periodsInRegulation / 2 + 1;
        const sorted = drives.slice().sort((a, b) => a.index - b.index);

        // Pass 1: pre-roll the opening drive of each half (and any overtime
        // period) to period start so the opening kickoff time is credited to the
        // receiving team's drive (NFL/NCAA convention: the receiving team's
        // possession begins at the kick), instead of being lost across the
        // period boundary or folded into the prior half's last drive. This
        // applies to the game opener, the second-half opener, and OT openers —
        // not mid-half period boundaries (e.g. Q1->Q2), where play continues
        // without a kick.
        // This MUST run before the end-time chaining below: pass 2 sets each
        // drive's endTime to the next drive's startTime, so the prior period's
        // last drive must see the pre-rolled (period-start) value — otherwise it
        // bleeds into the new period and the opening-kickoff seconds get counted
        // twice (once on the prior drive, once on the pre-rolled opener).
        sorted.forEach((drive, idx) => {
            const isGameOpener = drive.index === 1 && drive.startPeriod === 1;
            const isFirstDriveOfPeriod =
                idx === 0 || sorted[idx - 1].startPeriod !== drive.startPeriod;
            const isHalfOrOtOpener =
                isFirstDriveOfPeriod &&
                (drive.startPeriod === secondHalfStart ||
                    drive.startPeriod > periodsInRegulation);
            if (
                (isGameOpener || isHalfOrOtOpener) &&
                isNumber(drive.startTime) &&
                drive.startTime < periodLengthMs
            ) {
                drive.startTime = periodLengthMs;
            }
        });

        // Pass 2: chain end times and compute totals.
        sorted.forEach((drive, idx) => {
            const next = sorted[idx + 1];
            let endTime;
            let endPeriod;
            if (
                next &&
                isNumber(next.startTime) &&
                isNumber(next.startPeriod)
            ) {
                endTime = next.startTime;
                endPeriod = next.startPeriod;
            } else if (isNumber(drive.endTime) && isNumber(drive.endPeriod)) {
                endTime = drive.endTime;
                endPeriod = drive.endPeriod;
            } else {
                endTime = gameContext.getTime();
                endPeriod = gameContext.getCurrentPeriod();
            }
            drive.endTime = endTime;
            drive.endPeriod = endPeriod;
            drive.totalTime = getTimeDifference(
                drive.startTime,
                drive.startPeriod,
                endTime,
                endPeriod,
                minutesByPeriod
            );
        });
        return sorted;
    }

    async getIncompleteDrives(gameId) {
        return driveRepository
            .where({
                gameId: gameId
            })
            .filter((d) => !d.completed)
            .toArray();
    }

    async getByGameId(gameId, includePlayFragments) {
        const drives = await driveRepository
            .where({ gameId: gameId })
            .with({ plays: "play" });
        drives
            .sort((a, b) => a.index - b.index)
            .forEach((d) => d.plays.sort((a, b) => a.index - b.index));

        if (includePlayFragments) {
            await Promise.all(
                drives.map(async (d) => {
                    d.plays = await Promise.all(
                        d.plays.map(async (p) => {
                            return playRepository.getPlayWithFragments(p.id);
                        })
                    );
                })
            );
        }
        return drives;
    }

    _isStartOfPeriod(gameContext) {
        const { minutesByPeriod } = gameContext.gameConfig.general;
        return (
            gameContext.getTime() ===
            getPeriodStartSeconds(minutesByPeriod) * 1000
        );
    }

    _getPreviousEndTimeAndPeriod(previousDrive, gameContext) {
        // Always close the gap between the previous drive's end and the new
        // drive's start (PAT/kickoff clock belongs to the drive that just
        // ended). Override any stored endTime/endPeriod with the current
        // game clock at the moment this startDrive fires.
        let endTime = gameContext.getTime();
        let endPeriod = gameContext.getCurrentPeriod();

        if (
            previousDrive.startPeriod !== gameContext.getCurrentPeriod() &&
            this._isStartOfPeriod(gameContext)
        ) {
            endTime = 0;
            endPeriod = previousDrive.startPeriod;
        }

        return { endTime, endPeriod };
    }

    async startDrive(gameContext) {
        const { gameId, triggerKO, kickingOffTeamId } = gameContext;
        const startPeriod = gameContext.getCurrentPeriod();
        const startYard = triggerKO
            ? gameContext.gameConfig.spots.kickoff
            : gameContext.getFieldPosition();
        const startTime = gameContext.getTime();

        const driveIndex = await this._getDriveIndex(gameId);
        const previousDrive = await this.getPreviousDriveByIndex(
            gameId,
            driveIndex
        );
        if (previousDrive) {
            const { endTime, endPeriod } = this._getPreviousEndTimeAndPeriod(
                previousDrive,
                gameContext
            );
            await this.setEndTimeToDrive(
                previousDrive,
                endTime,
                endPeriod,
                gameContext.gameConfig.general.minutesByPeriod
            );
        }

        let drive = new Drive(gameId, driveIndex);
        drive.startType = triggerKO
            ? DriveStartType.Kickoff
            : previousDrive?.endType;
        drive.startPeriod = startPeriod;
        drive.startTime = startTime;
        drive.startYard = startYard;
        drive.homeTeamHasPossession = gameContext.homeTeamHasPossession;

        drive = await this._put(drive);
        if (kickingOffTeamId) await this._linkKO(gameId, drive);
        return drive;
    }

    /**
     *
     * @param gameId
     * @returns {Promise<number>} The next or first available index that a newly created Drive should use.
     * @private
     */
    async _getDriveIndex(gameId) {
        const drives = await driveRepository
            .where({ gameId: gameId })
            .toArray();
        const maxDriveIndex = Math.max(...drives.map((d) => d.index), 0);
        const indexes = Array.from(
            { length: maxDriveIndex + 1 },
            (_, index) => index
        ).map((i) => i + 1);

        return indexes.find((idx) => !drives.some((d) => d.index === idx));
    }

    async _linkKO(gameId, drive) {
        const plays = await playRepository.where({ gameId: gameId }).toArray();
        // Link the still-unassigned kickoff (and any dead-ball penalty entered
        // before the receiving team's first scrimmage play) to this new drive.
        // In the current model penalties are assigned to their drive at entry,
        // so only genuinely-unattached kickoff/penalty plays are swept here;
        // legacy games whose links were corrupted are repaired separately by
        // `repairDriveAssignments` / `normalizeLegacyDrives`.
        const unassociatedKOPlays = plays.filter(
            (p) =>
                p.driveId === null && p.historyContains(FootballState.KickOff)
        );
        const unassociatedPenaltyPlays = plays.filter(
            (p) =>
                p.driveId === null &&
                p.historyContains(FootballState.PlayPenalty)
        );
        const koStats = await statsRepository
            .where("playId")
            .anyOf(unassociatedKOPlays.map((koP) => koP.id))
            .toArray();

        await Promise.all([
            ...unassociatedPenaltyPlays.map(async (pp) => {
                pp.driveId = drive.id;
                await playRepository.put(pp);
            }),
            ...unassociatedKOPlays.map(async (kp) => {
                kp.driveId = drive.id;
                await playRepository.put(kp);
            }),
            ...koStats.map(async (koS) => {
                koS.driveId = drive.id;
                await statsRepository.put(koS);
            })
        ]);
    }

    async endDrive(gameContext, driveProps = {}) {
        const { endSpot } = driveProps;
        const endYard =
            endSpot || endSpot === 0 ? endSpot : gameContext.getFieldPosition();
        const endType = gameContext.getDriveContext().previousDriveEndType;

        const current = await this.getCurrentDrive(gameContext.gameId);
        if (current) {
            const tdTypes = [
                DriveEndType.Touchdown,
                DriveEndType.TurnoverOnDefenseForTouchdown
            ];
            if (tdTypes.some((t) => t.equals(endType))) {
                current.scoringTeamId = gameContext.getPossessionTeamId();
            }
            current.completed = true;
            current.endYard = endYard;
            current.endType = endType;
            return this._put(current);
        }

        return null;
    }

    async openDrive(driveId) {
        const drive = await this.getById(driveId);
        if (drive?.completed) {
            drive.completed = false;
            drive.endYard = null;
            drive.endType = null;
            drive.scoringTeamId = null;
            drive.endTime = null;
            drive.endPeriod = null;
            drive.totalTime = 0;
            return this._put(drive);
        }
        return null;
    }

    async _unlinkPlays(driveId) {
        const drivePlays = await playRepository
            .where({ driveId: driveId })
            .toArray();

        await playRepository.bulkPut(
            drivePlays.map((p) => Object.assign(p, { driveId: null }))
        );
    }

    async deleteDrive(gameId, driveId) {
        const prevDrive = await this.getPreviousDrive(driveId);
        if (prevDrive) {
            prevDrive.endPeriod = null;
            prevDrive.endTime = null;
            await this._put(prevDrive);
        }
        await this._delete(driveId);
        await this._unlinkPlays(driveId);
    }

    async saveDrive(drive) {
        return this._put(drive);
    }

    async setEndTimeToDrive(drive, endTime, endPeriod, minutesByPeriod) {
        if (drive) {
            drive.endTime = endTime;
            drive.endPeriod = endPeriod;
            drive.totalTime = getTimeDifference(
                drive.startTime,
                drive.startPeriod,
                endTime,
                endPeriod,
                minutesByPeriod
            );

            return this._put(drive);
        }

        return null;
    }

    async rebuildDriveAssignments(gameId) {
        const drives = await this.getByGameId(gameId);
        const knownDriveIds = new Set(drives.map((d) => d.id));

        const allPlays = await playRepository.where({ gameId }).toArray();
        allPlays.sort((a, b) => a.index - b.index);

        const allStats = await statsRepository.where({ gameId }).toArray();
        const statsByPlayId = {};
        for (const stat of allStats) {
            if (!statsByPlayId[stat.playId]) {
                statsByPlayId[stat.playId] = [];
            }
            statsByPlayId[stat.playId].push(stat);
        }

        let lastKnownDriveId = null;
        const fixedPlayIds = [];

        for (const play of allPlays) {
            if (play.driveId && knownDriveIds.has(play.driveId)) {
                lastKnownDriveId = play.driveId;
            } else if (play.driveId && !knownDriveIds.has(play.driveId)) {
                if (lastKnownDriveId) {
                    play.driveId = lastKnownDriveId;
                    await playRepository.put(play);
                    fixedPlayIds.push(play.id);

                    const playStats = statsByPlayId[play.id] || [];
                    for (const stat of playStats) {
                        if (stat.driveId && !knownDriveIds.has(stat.driveId)) {
                            stat.driveId = lastKnownDriveId;
                        }
                    }
                    if (playStats.length > 0) {
                        await statsRepository.bulkPut(playStats);
                    }
                }
            }
        }

        return { fixedPlayIds };
    }

    /**
     * Repair corrupted play->drive links on legacy games. The old unscoped
     * `_linkKO` sweep pulled unassigned penalty/kickoff plays into a later
     * drive, leaving plays pointing at a foreign drive (wrong drive-start
     * badges, drives spanning periods). Re-derive each play's driveId from the
     * legacy StartDrive/kickoff boundary markers and persist the corrections
     * (and their stats). Idempotent: a clean game makes no writes.
     * @param {string} gameId
     * @returns {Promise<{fixedPlayIds: Array<string>}>}
     */
    async repairDriveAssignments(gameId) {
        const plays = await playRepository.where({ gameId }).toArray();

        // Purge legacy EndDriveTime fragments already sitting in the local
        // store (recorded before PSTATS-842 removed the class): they revive
        // as plain objects and crash fragment method dispatch. The play's
        // history keeps its END_DRIVE_TIME entry so the remaining fragments'
        // associatedHistoryIndex links stay valid. Idempotent.
        const playIds = new Set(plays.map((play) => play.id));
        await fragmentRepository
            .getTable()
            .where({ associatedState: END_DRIVE_TIME_STATE })
            .and((fragment) => playIds.has(fragment.playId))
            .delete();

        const changes = deriveDriveLinks(
            plays.map((play) => ({
                id: play.id,
                index: play.index,
                driveId: play.driveId,
                isStartDrive: play.historyContains(FootballState.StartDrive),
                isKickOff: play.historyContains(FootballState.KickOff),
                isPlayPenalty: play.historyContains(FootballState.PlayPenalty)
            }))
        );

        const playById = new Map(plays.map((play) => [play.id, play]));
        const allStats = await statsRepository.where({ gameId }).toArray();

        const fixedPlayIds = [];
        if (changes.length) {
            const statsByPlayId = {};
            for (const stat of allStats) {
                if (!statsByPlayId[stat.playId]) {
                    statsByPlayId[stat.playId] = [];
                }
                statsByPlayId[stat.playId].push(stat);
            }

            for (const { id, driveId } of changes) {
                const play = playById.get(id);
                if (!play) {
                    continue;
                }
                play.driveId = driveId;
                await playRepository.put(play);
                fixedPlayIds.push(id);

                const playStats = statsByPlayId[id] || [];
                if (playStats.length) {
                    playStats.forEach((stat) => {
                        stat.driveId = driveId;
                    });
                    await statsRepository.bulkPut(playStats);
                }
            }
        }

        await this._repairStatDriveLinks(allStats, playById);
        await this._resequenceDriveIndexes(gameId);

        return { fixedPlayIds };
    }

    /**
     * Restamp drive indexes in chronological order. `_getDriveIndex` fills
     * gaps in the index sequence (the mechanism reintegration relies on to
     * recreate deleted drives in place), so a gap left behind by earlier
     * corruption can hand a live end-of-game drive a low index — which breaks
     * everything ordered by index: drive summary rows, their cumulative
     * scores, and drive total times (computed from the next drive's start).
     * Order by start period, then start clock (countdown: higher = earlier),
     * keeping current index order for ties (back-to-back drives share a
     * boundary timestamp). Idempotent: a clean game makes no writes.
     * @param {string} gameId
     * @private
     */
    async _resequenceDriveIndexes(gameId) {
        const drives = await driveRepository
            .where({ gameId: gameId })
            .toArray();
        const ordered = [...drives].sort(
            (a, b) =>
                (a.startPeriod ?? 0) - (b.startPeriod ?? 0) ||
                (b.startTime ?? 0) - (a.startTime ?? 0) ||
                a.index - b.index
        );
        const renumbered = ordered
            .map((drive, position) => ({ drive, index: position + 1 }))
            .filter(({ drive, index }) => drive.index !== index)
            .map(({ drive, index }) => Object.assign(drive, { index }));

        if (renumbered.length) {
            await driveRepository.bulkPut(renumbered);
        }
    }

    /**
     * Realign stats whose driveId drifted from their play's drive. Play
     * edits, reintegration, and drive repairs restamp `play.driveId`, but
     * stats keep the `gameContext.currentDriveId` they were created with, so
     * they can end up bucketed onto a drive whose plays don't include them
     * (wrong quarter/drive on scoring rows, boxscore serialization crashes).
     * Stats on drive-less plays (PAT/kickoff) keep their resolved driveId —
     * that attribution is intentional. Stats created with NO driveId (a
     * kickoff-family stat can be created before the receiving team's drive
     * exists, e.g. a deuce) adopt their play's drive once it has one, so they
     * surface in drive-scoped queries and per-drive scoring. When the play
     * has no drive either (a turnover-return touchdown: the turnover rule
     * nulls currentDriveId before the scoring stats are stamped), the stats
     * adopt the nearest preceding play's drive — the drive the turnover
     * ended — mirroring the _precedingPlayDriveId fallback used at entry.
     * Stats whose play no longer exists are corrupt leftovers and are
     * deleted. Idempotent: a clean game makes no writes.
     * @param {FootballStats[]} stats every stat record for the game
     * @param {Map<string, FootballPlay>} playById every play for the game
     * @private
     */
    async _repairStatDriveLinks(stats, playById) {
        const playsByIndex = [...playById.values()].sort(
            (a, b) => a.index - b.index
        );
        const precedingDriveId = (play) => {
            for (let i = playsByIndex.length - 1; i >= 0; i--) {
                const candidate = playsByIndex[i];
                if (candidate.index < play.index && candidate.driveId) {
                    return candidate.driveId;
                }
            }
            return null;
        };

        const drifted = [];
        const danglingIds = [];
        for (const stat of stats) {
            const play = playById.get(stat.playId);
            if (!play) {
                danglingIds.push(stat.id);
            } else if (
                play.driveId &&
                stat.driveId &&
                stat.driveId !== play.driveId
            ) {
                stat.driveId = play.driveId;
                drifted.push(stat);
            } else if (!stat.driveId) {
                const adoptedDriveId = play.driveId || precedingDriveId(play);
                if (adoptedDriveId) {
                    stat.driveId = adoptedDriveId;
                    drifted.push(stat);
                }
            }
        }
        if (drifted.length) {
            await statsRepository.bulkPut(drifted);
        }
        if (danglingIds.length) {
            await statsRepository.bulkDelete(danglingIds);
        }
    }

    async resolveDriveId(play, gameContext) {
        let currentDriveId;
        // A kickoff belongs to the receiving team's new drive, never the drive
        // that just ended. Leave it unassigned here so `_linkKO` links it when
        // that drive is created. Without this, a kickoff following a play that
        // does not finalize the drive before it (e.g. a field goal, which has
        // no PAT) would inherit the just-ended drive's id — making the scoring
        // play not the last play of its drive (wrong drive-start/end badges).
        if (play.historyContains(FootballState.KickOff)) {
            return null;
        }
        if (gameContext.currentDriveId) {
            currentDriveId = gameContext.currentDriveId;
        } else if (
            // Only kind of plays which use can create after a drive was closed are:
            play.historyContains(FootballState.PlayPenalty) ||
            play.historyContains(FootballState.PatTry) ||
            play.historyContains(FootballState.UpdateContext) ||
            play.historyContains(FootballState.Timeout) ||
            play.historyContains(FootballState.CoinToss)
        ) {
            // Prefer the drive of the play immediately preceding this one.
            // getLastDrive returns the highest-INDEX drive, which can be a
            // chronologically-later drive when the eager model creates drives
            // out of order (e.g. a PAT after a defensive-TD return otherwise
            // resolving to a future drive). In normal forward play the
            // preceding play's drive IS the last drive, so this only changes
            // the out-of-order case. Fall back to getLastDrive when there is no
            // preceding assigned play.
            const precedingDriveId = await this._precedingPlayDriveId(play);
            currentDriveId =
                precedingDriveId ?? (await this.getLastDrive(play.gameId))?.id;
        }
        return currentDriveId;
    }

    /**
     * driveId of the assigned play with the greatest index below this play's
     * index (the drive in progress just before it). Null when the play has no
     * index yet or no earlier assigned play exists.
     * @param {object} play
     * @returns {Promise<string|null>}
     */
    async _precedingPlayDriveId(play) {
        if (!isNumber(play?.index)) {
            return null;
        }
        const plays = await playRepository
            .where({ gameId: play.gameId })
            .toArray();
        const preceding = plays
            .filter(
                (p) =>
                    p.id !== play.id &&
                    isNumber(p.index) &&
                    p.index < play.index &&
                    p.driveId
            )
            .sort((a, b) => b.index - a.index)[0];
        return preceding?.driveId ?? null;
    }
}

export default new DriveService();
